import { useCallback, useMemo } from 'react';

import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';

import { DEFAULT_MESSAGE_GROUPS_BY_SEGMENT } from '@telialt/messages/src/constants';
import { DDComponent } from '@telialt/utils/src/datadog/setup';
import { useLogError } from '@telialt/utils/src/datadog/useLogError';
import { withMissingKeyLogging } from '@telialt/utils/src/hooks';
import { useSegment } from '@telialt/utils/src/segment';

import { apiGetMessages } from './api';
import { MESSAGES_QUERY_KEY } from './constants';
import { useLanguage } from './Language/useLanguage';
import { useMessageGroupsFromContext } from './MessageGroupsContext';
import { MessagesType } from './model';

const useMessagesQuery = (requestedGroups?: string[], enabled = true) => {
    const { language } = useLanguage();
    const segment = useSegment();
    const currentSegment = (language === 'en' ? 'english' : undefined) || segment;
    const defaultGroups = DEFAULT_MESSAGE_GROUPS_BY_SEGMENT[currentSegment];
    const contextGroups = useMessageGroupsFromContext();
    const groups = requestedGroups || contextGroups || defaultGroups;

    return useQuery({
        queryKey: [MESSAGES_QUERY_KEY, groups],
        queryFn: () => apiGetMessages(groups),
        retry: 0,
        refetchInterval: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
        enabled,
    });
};

export const useInitializeTranslations = (groups?: string[], enabled = true) => {
    const { data, status, error } = useMessagesQuery(groups, enabled);

    useLogError(error, DDComponent.Messages);

    const hasMessage = useCallback((messageKey: string) => !!data?.[messageKey], [data]);

    return {
        t: data ? withMissingKeyLogging(data) : data,
        translationsStatus: status,
        translationsError: error,
        hasMessage,
    };
};

/**
 * Gets all messages which are loaded in current react query context
 */
const useLoadedQueryMessages = () => {
    const queryClient = useQueryClient();

    const queriesData = useMemo(
        () =>
            queryClient.getQueriesData<MessagesType>({
                queryKey: [MESSAGES_QUERY_KEY],
                exact: false,
            }),
        [queryClient],
    );

    const filteredQueries = useMemo(() => queriesData.filter(([_, messages]) => !!messages), [queriesData]);

    const allMessages = useMemo(
        () =>
            filteredQueries.reduce((acc, [_, messages]) => {
                return { ...acc, ...messages };
            }, {}),
        [filteredQueries],
    ) as MessagesType;

    const hasMessage = useCallback((messageKey: string) => !!allMessages[messageKey], [allMessages]);

    return { t: filteredQueries.length > 0 ? withMissingKeyLogging(allMessages) : undefined, hasMessage };
};

export const useTranslations = () => {
    const { isAppWithLanguageSwitch } = useLanguage();
    const { t: initializedT, hasMessage: initializedHasMessage } = useInitializeTranslations(undefined, false);

    const { t: messagesT, hasMessage: messagesHasMessage } = useLoadedQueryMessages();
    const t = isAppWithLanguageSwitch ? initializedT : messagesT;
    const hasMessage = isAppWithLanguageSwitch ? initializedHasMessage : messagesHasMessage;

    if (!t) {
        throw new Error('useInitializeTranslations should be called before using useTranslations');
    }

    return { t, hasMessage };
};
